
/*
 * Nettie's Packaging v1.7
 *
 * Supports two packaging fulfillment modes:
 *
 * variant:
 *   Fixed assortments use matching occasion SKUs.
 *
 * property:
 *   Custom boxes keep their original SKU and
 *   save packaging as Shopify line-item properties.
 *
 * Packaging is included in property mode.
 */

(function () {
  'use strict';

  const VERSION = '1.7';

  function initialize(root) {
    if (root.dataset.initialized === 'true') return;

    const dataElement = document.getElementById(
      root.id.replace('netties-packaging-', 'np-data-')
    );

    if (!dataElement) return;

    let data;

    try {
      data = JSON.parse(dataElement.textContent);
    } catch (error) {
      console.error(
        '[Netties Packaging] Invalid data',
        error
      );
      return;
    }

    const fulfillmentMode =
      data.fulfillmentMode === 'property'
        ? 'property'
        : 'variant';

    const propertyMode =
      fulfillmentMode === 'property';

    const section = root.closest('.index-product');

    const form = document.getElementById(
      root.dataset.formId
    );

    if (!section || !form) {
      console.warn(
        '[Netties Packaging] Product form not found'
      );
      return;
    }

    const variantInput = Array.from(form.elements)
      .find(element => element.name === 'id');

    const addButton = form.querySelector(
      'button[data-add-to-cart]'
    );

    if (!variantInput || !addButton) {
      console.warn(
        '[Netties Packaging] Missing purchase controls'
      );
      return;
    }

    const optionsElement = root.querySelector(
      '.np-options'
    );

    const confirmation = root.querySelector(
      '.np-price-note'
    );

    const message = root.querySelector(
      '.np-message'
    );

    if (!optionsElement || !confirmation || !message) {
      return;
    }


    /*
     * Sleeve carousel pagination.
     * Matches Elevate collection carousel dots.
     */

    const pagination = document.createElement('div');
    pagination.className = 'np-pagination';
    pagination.setAttribute('aria-label', 'Sleeve carousel pagination');
    optionsElement.insertAdjacentElement('afterend', pagination);

    let paginationFrame = 0;

    function updatePagination() {
      const dots = Array.from(pagination.children);
      const maxScroll = Math.max(
        0,
        optionsElement.scrollWidth - optionsElement.clientWidth
      );

      const scrollable = maxScroll > 2 && dots.length > 1;
      pagination.hidden = !scrollable;

      if (!scrollable) return;

      const progress = Math.min(
        1,
        Math.max(0, optionsElement.scrollLeft / maxScroll)
      );

      const activeIndex = Math.round(progress * (dots.length - 1));

      dots.forEach((dot, index) => {
        const active = index === activeIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', String(active));
      });
    }

    function requestPaginationUpdate() {
      if (paginationFrame) return;

      paginationFrame = requestAnimationFrame(() => {
        paginationFrame = 0;
        updatePagination();
      });
    }

    function renderPagination(count) {
      if (pagination.children.length !== count) {
        pagination.replaceChildren();

        for (let index = 0; index < count; index++) {
          const dot = document.createElement('button');

          dot.type = 'button';
          dot.className = 'np-pagination__dot';
          dot.setAttribute(
            'aria-label',
            'Scroll sleeve options to position ' + (index + 1)
          );

          dot.addEventListener('click', () => {
            const maxScroll = Math.max(
              0,
              optionsElement.scrollWidth - optionsElement.clientWidth
            );

            optionsElement.scrollTo({
              left: count > 1
                ? maxScroll * index / (count - 1)
                : 0,
              behavior: window.matchMedia(
                '(prefers-reduced-motion: reduce)'
              ).matches ? 'auto' : 'smooth'
            });
          });

          pagination.appendChild(dot);
        }
      }

      requestPaginationUpdate();
    }

    optionsElement.addEventListener(
      'scroll',
      requestPaginationUpdate,
      { passive: true }
    );

    window.addEventListener('resize', requestPaginationUpdate);


    const productTitle =
      root.dataset.productTitle?.trim() || 'Your box';

    const sleeves = (data.sleeves || [])
      .filter(item =>
        item.name &&
        item.suffix &&
        Array.isArray(item.variants)
      )
      .sort((a, b) =>
        Number(a.order) - Number(b.order)
      );

    const formatter = new Intl.NumberFormat(
      document.documentElement.lang || 'en-US',
      {
        style: 'currency',
        currency: data.currency || 'USD'
      }
    );

    const money = cents =>
      formatter.format(Number(cents) / 100);

    let selectedSuffix = '';
    let selectedTarget = null;
    let currentBase = null;
    let lastVariantId = '';
    let submitting = false;

    /*
     * Property-mode line-item inputs.
     *
     * Both controls are associated with the
     * existing product form so FormData(form)
     * includes them.
     */

    let packagingInput = null;
    let packagingCodeInput = null;

    if (propertyMode) {
      packagingInput = document.createElement('input');
      packagingInput.type = 'hidden';
      packagingInput.name = 'properties[Packaging]';
      packagingInput.setAttribute('form', form.getAttribute('id'));

      packagingCodeInput =
        document.createElement('input');

      packagingCodeInput.type = 'hidden';
      packagingCodeInput.name =
        'properties[Packaging Code]';

      packagingCodeInput.setAttribute(
        'form',
        form.getAttribute('id')
      );

      root.append(
        packagingInput,
        packagingCodeInput
      );
    }

    function getBase() {
      return data.variants.find(
        variant =>
          String(variant.id) ===
          String(variantInput.value)
      ) || null;
    }

    function resolve(sleeve, base) {
      if (!base || !base.sku) return null;

      const destinationSku =
        base.sku + '-' + sleeve.suffix;

      return sleeve.variants.find(
        variant =>
          variant.sku === destinationSku &&
          variant.available === true
      ) || null;
    }

    function priceDifferenceText(difference) {
      if (difference > 0) {
        return '+' + money(difference);
      }

      if (difference < 0) {
        return '−' + money(Math.abs(difference));
      }

      return 'No price change';
    }

    function updateProperties(choice) {
      if (!propertyMode) return;

      packagingInput.value =
        choice.suffix ? choice.name : 'Standard';

      packagingCodeInput.value =
        choice.suffix || 'STANDARD';
    }

    function makeChoice(choice) {
      const button = document.createElement('button');

      button.type = 'button';
      button.className = 'np-choice';
      button.dataset.suffix = choice.suffix;

      const choiceLabel = choice.suffix
        ? choice.name + ' · ' +
          (propertyMode
            ? 'Included'
            : priceDifferenceText(choice.delta))
        : 'No gift wrapping · Included';

      button.setAttribute(
        'aria-label',
        choiceLabel
      );

      button.title = choiceLabel;

      button.setAttribute(
        'aria-pressed',
        String(selectedSuffix === choice.suffix)
      );

      if (choice.image) {
        const image = document.createElement('img');

        image.className = 'np-choice-image';
        image.src = choice.image;
        image.alt = '';
        image.loading = 'lazy';

        button.appendChild(image);
      } else {
        const icon = document.createElement('span');

        icon.className = 'np-standard-icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.textContent = '×';

        button.appendChild(icon);
      }

      const name = document.createElement('span');

      name.className = 'np-choice-name';
      name.textContent = choice.name;

      button.appendChild(name);

      const delta = document.createElement('span');

      delta.className = 'np-choice-delta';

      delta.textContent = propertyMode
        ? 'Included'
        : choice.suffix
          ? priceDifferenceText(choice.delta)
          : 'Included';

      button.appendChild(delta);

      button.addEventListener('click', () => {
        if (submitting) return;

        const positionBefore =
          button.getBoundingClientRect().top;

        selectedSuffix = choice.suffix;

        render();

        const selectedButton = Array.from(
          optionsElement.querySelectorAll('.np-choice')
        ).find(el =>
          el.dataset.suffix === selectedSuffix
        );

        if (!selectedButton) return;

        const positionAfter =
          selectedButton.getBoundingClientRect().top;

        const difference =
          positionAfter - positionBefore;

        if (Math.abs(difference) > 1) {
          window.scrollBy({
            top: difference,
            behavior: 'instant'
          });
        }
      });

      return button;
    }

    function renderConfirmation(choice) {
      confirmation.replaceChildren();

      confirmation.classList.remove(
        'np-price-note--selected'
      );

      if (!selectedSuffix) return;

      if (choice.image) {
        const image = document.createElement('img');

        image.className = 'np-confirmation-image';
        image.src = choice.image;

        image.alt =
          choice.name + ' sleeve artwork';

        image.loading = 'lazy';

        confirmation.appendChild(image);
      }

      const content = document.createElement('div');

      content.className = 'np-confirmation-content';

      const heading = document.createElement('strong');

      heading.className = 'np-confirmation-heading';

      heading.textContent =
        choice.name +
        (!propertyMode && choice.delta !== 0
          ? ' · ' + priceDifferenceText(choice.delta)
          : '');

      const description = document.createElement('p');

      description.className =
        'np-confirmation-description';

      description.textContent =
        productTitle +
        ' will come wrapped in the ' +
        choice.name +
        ' sleeve.';

      const pricing = document.createElement('p');

      pricing.className = 'np-confirmation-pricing';

      pricing.textContent = propertyMode
        ? 'Gift wrapping included. Add a personal message in the cart.'
        : 'Add a personal message in the cart.';

      content.append(
        heading,
        description,
        pricing
      );

      confirmation.appendChild(content);

      confirmation.classList.add(
        'np-price-note--selected'
      );
    }

    function render() {
      const base = getBase();

      if (!base) {
        root.hidden = true;
        currentBase = null;
        selectedTarget = null;
        return;
      }

      currentBase = base;

      const choices = [{
        name: 'Standard',
        suffix: '',
        image: null,
        delta: 0,
        target: base
      }];

      sleeves.forEach(sleeve => {
        const target = propertyMode
          ? base
          : resolve(sleeve, base);

        if (!target) return;

        choices.push({
          name: sleeve.name,
          suffix: sleeve.suffix,
          image: sleeve.image,
          delta: propertyMode
            ? 0
            : target.price - base.price,
          target
        });
      });

      if (!choices.some(
        choice => choice.suffix === selectedSuffix
      )) {
        selectedSuffix = '';
      }

      const active = choices.find(
        choice => choice.suffix === selectedSuffix
      ) || choices[0];

      selectedTarget = active.target;

      optionsElement.replaceChildren();

      choices.forEach(choice => {
        optionsElement.appendChild(
          makeChoice(choice)
        );
      });

      renderConfirmation(active);
      updateProperties(active);

      message.textContent = '';
      root.hidden = false;

      renderPagination(choices.length);
    }

    /*
     * Fixed-assortment purchases retain
     * their existing occasion-variant flow.
     *
     * Property mode does not intercept
     * Add to Cart. The Box Builder's
     * purchase lock remains responsible
     * for preventing checkout during testing.
     */

    async function addSelectedPackaging() {
      if (
        propertyMode ||
        !selectedSuffix ||
        submitting
      ) {
        return;
      }

      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      const base = getBase();

      const sleeve = sleeves.find(
        item => item.suffix === selectedSuffix
      );

      const target = base && sleeve
        ? resolve(sleeve, base)
        : null;

      if (!target) {
        message.textContent =
          'This packaging option is unavailable. ' +
          'Please select another option.';
        return;
      }

      submitting = true;
      message.textContent = 'Adding your box…';

      try {
        const formData = new FormData(form);

        formData.set(
          'id',
          String(target.id)
        );

        const rootUrl =
          window.Shopify?.routes?.root || '/';

        const response = await fetch(
          rootUrl + 'cart/add.js',
          {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'X-Requested-With':
                'XMLHttpRequest',
              'Accept':
                'application/json'
            },
            body: formData
          }
        );

        const result = await response.json();

        if (!response.ok || result.status) {
          throw new Error(
            result.description ||
            result.message ||
            'Unable to add your box.'
          );
        }

        console.info(
          '[Netties Packaging] Added to cart',
          {
            sku: target.sku,
            variantId: target.id
          }
        );

        window.location.assign(
          rootUrl + 'cart'
        );

      } catch (error) {
        console.error(
          '[Netties Packaging] Cart error',
          error
        );

        message.textContent =
          error.message ||
          'Unable to add your box. Please try again.';

        submitting = false;
      }
    }

    function onClick(event) {
      if (
        propertyMode ||
        !selectedSuffix
      ) {
        return;
      }

      if (!(event.target instanceof Element)) {
        return;
      }

      const button = event.target.closest(
        'button[data-add-to-cart]'
      );

      if (button !== addButton || button.disabled) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      addSelectedPackaging();
    }

    function onSubmit(event) {
      if (
        propertyMode ||
        !selectedSuffix ||
        event.target !== form
      ) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      addSelectedPackaging();
    }

    document.addEventListener(
      'click',
      onClick,
      true
    );

    document.addEventListener(
      'submit',
      onSubmit,
      true
    );

    variantInput.addEventListener(
      'change',
      render
    );

    const timer = setInterval(() => {
      if (!root.isConnected) {
        clearInterval(timer);
        return;
      }

      const id = String(variantInput.value);

      if (id !== lastVariantId) {
        lastVariantId = id;
        render();
      }
    }, 300);

    section.classList.add('np-poc-enabled');

    root.dataset.initialized = 'true';

    render();

    window.nettiesPackagingPOC = {
      version: VERSION,

      getState() {
        return {
          productTitle,
          fulfillmentMode,
          base: currentBase,
          sleeve: selectedSuffix,
          target: selectedTarget,
          sleeveEntries: sleeves.length,
          formId: form.getAttribute('id')
        };
      },

      refresh() {
        render();
      }
    };

    console.info(
      '[Netties Packaging] v1.7 initialized',
      { fulfillmentMode }
    );
  }

  function start() {
    document.querySelectorAll(
      '[data-np-selector]'
    ).forEach(initialize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true }
    );
  } else {
    start();
  }
})();
